import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bell, User, LogOut, ChevronDown, Building, Check } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

import { Button } from '@/components/ui/button';
// import { SidebarTrigger } from "@/components/ui/sidebar"; -- Temporarily disabled for debugging
import GlobalSearch from './GlobalSearch';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useCompany } from '@/contexts/CompanyContext';

interface Notification {
  id: string;
  user_id: string;
  tenant_id: string;
  interview_session_id: string;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  updated_at: string;
  metadata?: {
    session_id?: string;
    position_id?: string;
    candidate_id?: string;
    [key: string]: any;
  };
}

const DashboardHeader: React.FC = () => {
  const { user, signOut } = useAuth();
  const { companies, selectedCompany, setSelectedCompany, loading } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleCompanyChange = (company: any) => {
    setSelectedCompany(company);
  };

  // Fetch unread notification count
  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) {
        console.error('Error fetching unread count:', error);
      } else {
        setUnreadCount(count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [user?.id]);

  // Fetch notifications for the current user
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;

    setNotificationsLoading(true);
    try {
      // Fetch both notifications and count in parallel
      const [notificationsResponse] = await Promise.all([
        supabase
          .from('notifications')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_read', false)
          .order('created_at', { ascending: false })
          .limit(50), // Limit UI display to 50 most recent notifications for performance
        fetchUnreadCount(), // Fetch accurate count separately
      ]);

      const { data, error } = notificationsResponse;

      if (error) {
        console.error('Error fetching notifications:', error);
      } else {
        setNotifications(data || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  }, [user?.id, fetchUnreadCount]);

  useEffect(() => {
    fetchNotifications();

    // Listen for manual refresh events from billing notifications
    const handleRefreshNotifications = () => {
      console.log('[DashboardHeader] Manual notification refresh triggered');
      fetchNotifications();
    };

    window.addEventListener('refreshNotifications', handleRefreshNotifications);

    // Set up real-time subscription for new notifications
    // Only subscribe if user is available to prevent repeated failed connections
    if (!user?.id) {
      return () => {
        window.removeEventListener('refreshNotifications', handleRefreshNotifications);
      };
    }

    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        payload => {
          const newNotification = payload.new as Notification;
          setNotifications(prev => [newNotification, ...prev]);

          // Increment unread count when new notification arrives
          setUnreadCount(prev => prev + 1);

          // Show toast with action for billing notifications
          const isBillingNotification =
            newNotification.type.startsWith('billing_') &&
            (newNotification.metadata?.billingEvent ||
              newNotification.type === 'billing_trial_ended' ||
              newNotification.type === 'billing_trial_ending_soon');

          if (isBillingNotification) {
            toast.info(newNotification.title, {
              description: newNotification.message,
              action: {
                label: 'View Billing',
                onClick: () => navigate('/company-settings#billing'),
              },
              duration: 8000, // Longer duration for billing notifications
            });
          } else {
            toast.info(newNotification.title, {
              description: newNotification.message,
            });
          }
        }
      )
      .subscribe((status, err) => {
        if (status === 'CHANNEL_ERROR') {
          console.debug('Realtime subscription error (this is normal in local development):', err);
        } else if (status === 'SUBSCRIBED') {
          console.debug('Successfully subscribed to notifications');
        }
      });

    return () => {
      // Remove manual refresh event listener
      window.removeEventListener('refreshNotifications', handleRefreshNotifications);

      // Improved cleanup with error handling
      if (channel) {
        try {
          // Use unsubscribe for graceful cleanup regardless of connection state
          channel.unsubscribe();
        } catch (error) {
          // Silently handle cleanup errors to prevent console noise
          console.debug('Channel cleanup handled:', error);
        }
      }
    };
  }, [user?.id, fetchNotifications]); // Use stable user.id instead of entire user object

  // Mark a single notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) {
        console.error('Error marking notification as read:', error);
      } else {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        // Decrement unread count when marking as read
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!user) return;

    // Don't check notifications.length === 0 because there might be more unread notifications
    // in the database than what's loaded in the UI due to pagination limits

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) {
        console.error('Error marking all notifications as read:', error);
        toast.error('Failed to mark all notifications as read');
      } else {
        // Refresh notifications from database to get accurate state
        await fetchNotifications();
        toast.success('All notifications marked as read');
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    await markAsRead(notification.id);

    // Handle interview assessment notifications
    if (notification.type === 'interview_feedback_received' && notification.metadata) {
      try {
        // Extract session_id from notification metadata
        const sessionId = notification.metadata.session_id || notification.interview_session_id;

        if (sessionId) {
          // Get position_id from the interview session
          const { data: sessionData, error } = await supabase
            .from('interview_sessions')
            .select('position_id, candidate_id')
            .eq('id', sessionId)
            .single();

          if (error || !sessionData) {
            console.error('Error fetching session data:', error);
            toast.error('Unable to find interview session');
            return;
          }

          // Navigate to position details with candidates tab active
          // Pass candidate_id and session_id as URL parameters to trigger assessment modal
          navigate(
            `/positions/${sessionData.position_id}?candidateId=${sessionData.candidate_id}&sessionId=${sessionId}&showAssessment=true#candidates`
          );
          return;
        }
      } catch (error) {
        console.error('Error handling interview assessment notification:', error);
        toast.error('Unable to navigate to assessment');
      }
    }

    // Handle expired interview invitation notifications
    if (
      notification.type === 'interview_invitation_expired' &&
      notification.metadata?.position_id
    ) {
      navigate(`/positions/${notification.metadata.position_id}#candidates`);
      return;
    }

    // Handle job board application notifications - navigate to specific candidate
    if (
      (notification.type === 'job_board_application_needs_review' ||
        notification.type === 'job_board_application_high_score') &&
      notification.metadata?.candidate_id
    ) {
      navigate(`/candidates/${notification.metadata.candidate_id}`);
      return;
    }

    // Default navigation for other notification types
    navigate(`/candidates`);
  };

  // Extract initials for avatar fallback
  const getUserInitials = () => {
    if (!user?.email) return 'U';
    return user.email.charAt(0).toUpperCase();
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-14 items-center justify-between px-6">
        <div className="flex items-center gap-4">
          {/* <SidebarTrigger className="md:hidden" /> -- Temporarily disabled for debugging */}

          <div className="flex items-center gap-2">
            <GlobalSearch />
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Company Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 gap-1">
                <Building className="h-4 w-4" />
                <span className="max-w-[150px] truncate">
                  {loading ? 'Loading...' : selectedCompany?.name || 'Select Company'}
                </span>
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Companies</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {companies.length > 0 ? (
                companies.map(company => (
                  <DropdownMenuItem
                    key={company.id}
                    className="cursor-pointer"
                    onClick={() => handleCompanyChange(company)}
                  >
                    {company.name}
                    {selectedCompany?.id === company.id && (
                      <span className="ml-auto pl-2 text-xs">✓</span>
                    )}
                  </DropdownMenuItem>
                ))
              ) : (
                <DropdownMenuItem disabled>No companies found</DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/companies')}>
                Manage Companies
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Notification Bell */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative h-8 w-8">
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
                    {unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>Notifications</span>
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                    onClick={e => {
                      e.preventDefault();
                      markAllAsRead();
                    }}
                  >
                    Mark all as read
                  </Button>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {notificationsLoading ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Loading notifications...
                </div>
              ) : notifications.length > 0 ? (
                <div className="max-h-[400px] overflow-y-auto">
                  {notifications.map(notification => (
                    <DropdownMenuItem
                      key={notification.id}
                      className="flex items-start gap-3 p-3 cursor-pointer hover:bg-blue-50 focus:bg-blue-50 hover:text-gray-900 focus:text-gray-900"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                        <p className="text-xs text-gray-600 mt-1">{notification.message}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(notification.created_at).toLocaleString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        onClick={e => {
                          e.stopPropagation();
                          markAsRead(notification.id);
                        }}
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                    </DropdownMenuItem>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No new notifications
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.user_metadata?.avatar_url} alt={user?.email || 'User'} />
                  <AvatarFallback>{getUserInitials()}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-72" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p
                    className="text-sm font-medium leading-none break-words hyphens-auto max-w-full"
                    title={user?.email}
                  >
                    {user?.email}
                  </p>
                  <p
                    className="text-xs leading-none text-muted-foreground break-words hyphens-auto max-w-full opacity-75"
                    title={user?.email}
                  >
                    {user?.email && user.email.length > 35
                      ? `${user.email.substring(0, 32)}...`
                      : user?.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;
